import {formatMidasNumber, displayHFRValue, displayPHSValue, convertDateFormat} from "../../global";
//import MoreOptions from "@mui/icons-material/MoreVert";
import {useCallback, useEffect, useRef, useState} from "react";
import { UXDataTableWithoutBody, StyledTableCell, UXDataTableNested, StyledTableBody, StyledTableRow } from "../../components/UXDataTable";
import {IconButton,TableRow, Checkbox} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import WorkRequest from "../../api/WorkRequest/WorkRequest";

const requestCols = ['ID', 'Samples', 'Request Type', 'Business', 'Cost Code', 'Submitted By', 'Requested Completion','Requested By', 'Assigned To', 'Assigned Date']
const sampleCols = ['MIDAS Number', 'Container #', 'Sample Description', 'Additional Info','Chem ID','Parent Sample', 'HFR', 'PHS', 'Perox', 'Sample Owner', 'Project']

const SearchRequests = ({inputSearchCriteria, myWorkRequests, dataIsLoading}) => {
  const [sampleRowOpen, setSampleRowOpen] = useState([]);
  const [requestDetails, setRequestDetails] = useState({});
  const [requestDetailsLoading, setRequestDetailsLoading] = useState({});
  const requestDetailsRef = useRef(requestDetails);
  const requestDetailsLoadingRef = useRef(requestDetailsLoading);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const noDataMessage = `No work request info found matching the quick search for = ${inputSearchCriteria}`

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  useEffect(() => {
    requestDetailsRef.current = requestDetails;
  }, [requestDetails]);

  useEffect(() => {
    requestDetailsLoadingRef.current = requestDetailsLoading;
  }, [requestDetailsLoading]);

  const loadRequestDetails = useCallback((workRequestID) => {
    if (
      requestDetailsRef.current[workRequestID] ||
      requestDetailsLoadingRef.current[workRequestID]
    ) {
      return;
    }

    requestDetailsLoadingRef.current = {
      ...requestDetailsLoadingRef.current,
      [workRequestID]: true,
    };
    setRequestDetailsLoading((prev) => ({ ...prev, [workRequestID]: true }));

    WorkRequest.getById(workRequestID)
      .then((response) => {
        if (response) {
          requestDetailsRef.current = {
            ...requestDetailsRef.current,
            [workRequestID]: response,
          };
          setRequestDetails((prev) => ({ ...prev, [workRequestID]: response }));
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        requestDetailsLoadingRef.current = {
          ...requestDetailsLoadingRef.current,
          [workRequestID]: false,
        };
        setRequestDetailsLoading((prev) => ({ ...prev, [workRequestID]: false }));
      });
  }, []);

  const handleSamplesRowClick = (workRequestID) => {
    const sampleRowOpenIndex = sampleRowOpen.indexOf(workRequestID);
    let newSampleRowOpen = [];
    const shouldOpenRow = sampleRowOpenIndex === -1;

    if (shouldOpenRow) {
      newSampleRowOpen = newSampleRowOpen.concat(sampleRowOpen, workRequestID);
    } else if (sampleRowOpenIndex === 0) {
      newSampleRowOpen = newSampleRowOpen.concat(sampleRowOpen.slice(1));
    } else if (sampleRowOpenIndex === sampleRowOpen.length - 1) {
      newSampleRowOpen = newSampleRowOpen.concat(sampleRowOpen.slice(0, -1));
    } else if (sampleRowOpenIndex > 0) {
      newSampleRowOpen = newSampleRowOpen.concat(
        sampleRowOpen.slice(0, sampleRowOpenIndex),
        sampleRowOpen.slice(sampleRowOpenIndex + 1)
      );
    }
    setSampleRowOpen(newSampleRowOpen);

    if (shouldOpenRow) {
      loadRequestDetails(workRequestID);
    }
  };

  const determineStartArray = () => {
    return (myWorkRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage))
  }

  useEffect(() => {
    const visibleRequests = myWorkRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    visibleRequests.forEach((workRequest) => loadRequestDetails(workRequest.id));
  }, [myWorkRequests, page, rowsPerPage, loadRequestDetails]);

  const isValidContainerNumber = (value) => {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    return true;
  };

  const normalizeContainerNumber = (value) =>
    typeof value === 'string' ? value.trim() : value;

  const getContainerNumber = (workRequestSample, workRequest) => {
    const sampleName = workRequestSample?.sample?.sampleName ?? workRequestSample?.sampleName;
    const sampleContainers = workRequestSample?.sample?.containers ?? [];
    const sampleContainerById = sampleContainers.find(
      (container) => container?.id === workRequestSample?.existingContainerID
    );

    const shippingContainerNumbers = (workRequest?.shippingRequest?.shippingItems ?? [])
      .filter((item) => {
        const itemSampleName = item?.sampleName ?? item?.thisContainer?.sampleName ?? item?.thisContainer?.sample?.sampleName;
        return sampleName && itemSampleName === sampleName;
      })
      .map((item) => {
        if (isValidContainerNumber(item?.thisContainer?.containerNumber)) {
          return normalizeContainerNumber(item.thisContainer.containerNumber);
        }

        if (item?.containerID) {
          const foundContainer = sampleContainers.find((container) => container?.id === item.containerID);
          if (isValidContainerNumber(foundContainer?.containerNumber)) {
            return normalizeContainerNumber(foundContainer.containerNumber);
          }
        }

        return undefined;
      })
      .filter(isValidContainerNumber);

    const directCandidates = [
      workRequestSample?.existingContainer?.containerNumber,
      workRequestSample?.newContainer?.containerNumber,
      workRequestSample?.containerNumber,
      workRequestSample?.thisContainer?.containerNumber,
      sampleContainerById?.containerNumber,
    ]
      .filter(isValidContainerNumber)
      .map(normalizeContainerNumber);

    const allCandidates = [...shippingContainerNumbers, ...directCandidates];
    const uniqueCandidates = [...new Set(allCandidates)];
    const isAnyContainerRequest =
      !workRequestSample?.existingContainerID &&
      !workRequestSample?.newContainerID &&
      !workRequestSample?.containerID;

    if (uniqueCandidates.length > 0) {
      return uniqueCandidates.join(', ');
    }

    return isAnyContainerRequest ? 'Any' : '';
  };

  const mergeRequestSamples = (baseSamples = [], detailedSamples = []) => {
    const sampleMap = new Map();

    baseSamples.forEach((sample, index) => {
      const key = sample?.id ?? `base-${index}`;
      sampleMap.set(key, sample);
    });

    detailedSamples.forEach((sample, index) => {
      const key = sample?.id ?? `details-${index}`;
      const existingSample = sampleMap.get(key);

      sampleMap.set(
        key,
        existingSample
          ? {
            ...existingSample,
            ...sample,
            sample: sample?.sample ?? existingSample?.sample,
          }
          : sample
      );
    });

    return Array.from(sampleMap.values());
  };

  const isSampleRowOpen = (workRequestID) => sampleRowOpen.indexOf(workRequestID) !== -1;

  return (
    <>
    <UXDataTableWithoutBody 
      tableWidth='100%' 
      cols={requestCols} 
      blankFirstHeader={false}
      tableId="myWRTable"
      rowLength={myWorkRequests ? myWorkRequests.length : 0}
      enablePaging={true}
      rowsPerPage={rowsPerPage}
      page={page}
      handleChangePage={handleChangePage}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      noDataFoundMessage={noDataMessage}
      enableAddIcon={false}
      addFunction={null}
      addToolTipText={null}  
      isDataLoading={dataIsLoading}
      >

        {myWorkRequests && myWorkRequests.length > 0 ? (
          determineStartArray().map((workRequest, index) => {
            const requestWithDetails = requestDetails[workRequest.id] ?? workRequest;
            const requestSamples = mergeRequestSamples(
              workRequest?.workRequestSamples ?? [],
              requestWithDetails?.workRequestSamples ?? []
            );
           
            return (
              <StyledTableBody key={"myWRTablebody" + index}> 
                <StyledTableRow>
                  <StyledTableCell>{workRequest.id}</StyledTableCell>

                    <StyledTableCell style={{width:'5rem'}}>

                    {requestSamples.length}

                      <IconButton
                        aria-label='expand row'
                        size='small'
                        onClick={() => handleSamplesRowClick(workRequest.id)}>
                        {isSampleRowOpen(workRequest.id) ? (
                          <KeyboardArrowUpIcon/>
                        ) : (
                          <KeyboardArrowDownIcon/>
                        )}
                      </IconButton>
                    </StyledTableCell>
                          
                    <StyledTableCell>{workRequest.typeName}</StyledTableCell>
                    <StyledTableCell>{workRequest.businessUnitName}</StyledTableCell>
                    <StyledTableCell>{workRequest.chargeCode}</StyledTableCell>
                    <StyledTableCell>{workRequest.createdByEmail}</StyledTableCell>
                    <StyledTableCell>{convertDateFormat(workRequest.requestedCompletionDate, false)}</StyledTableCell>
                    <StyledTableCell>{workRequest.requestedByEmail}</StyledTableCell>
                    <StyledTableCell>{workRequest.assignedToEmail}</StyledTableCell>
                    <StyledTableCell>{workRequest.assignedDate}</StyledTableCell>
                  </StyledTableRow>

                {isSampleRowOpen(workRequest.id) && (
                  <UXDataTableNested 
                  tableWidth={'100%'} 
                  cols={sampleCols} 
                  blankFirstHeader={false} 
                  colSpan={requestCols.length + 1} 
                  isOpenComparator={workRequest.id} 
                  isOpenArray={sampleRowOpen}>

                    {requestSamples.map((oSample, sampleIndex) => (
                    <StyledTableBody key={`${workRequest.id}_${oSample.id ?? sampleIndex}_testtablebody`}>
                      <StyledTableRow>
                        <StyledTableCell sx={{minWidth:"8rem"}}>{formatMidasNumber(oSample.sample?.sampleName ?? oSample.sampleName)}</StyledTableCell>
                        <StyledTableCell>{getContainerNumber(oSample, requestWithDetails)}</StyledTableCell>
                        <StyledTableCell>{oSample.sample?.description}</StyledTableCell>
                        <StyledTableCell>{oSample.sample?.additionalSampleInformation}</StyledTableCell>
                        <StyledTableCell>{oSample.sample?.chemID}</StyledTableCell>
                        <StyledTableCell>{oSample.sample?.parentSampleContainer ? oSample.sample.parentSampleContainer.containerNumber : ''}</StyledTableCell>
                        <StyledTableCell>
                          {oSample.sample?.substance ? displayHFRValue(oSample.sample.substance): ('?-?-?')}
                        </StyledTableCell>
                        <StyledTableCell>{oSample.sample?.substance ? displayPHSValue(oSample.sample.substance) : ('???')}</StyledTableCell>
                        <StyledTableCell>
                          <Checkbox color='primary' checked={oSample.sample?.substance && oSample.sample.substance.isPeroxidizable ? true : false} disabled={true} />
                        </StyledTableCell>
                        <StyledTableCell>{oSample.sample?.sampleOwnerEmail}</StyledTableCell>
                        <StyledTableCell>{oSample.sample?.project ? oSample.sample.project.name : oSample.sample?.projectID}</StyledTableCell>
                      </StyledTableRow>
                      
                      </StyledTableBody>
                      ))}
                      </UXDataTableNested>
                    )}
                
                </StyledTableBody>
            );
          })
        ) : (
          <StyledTableBody key={"no WR found"}>
            <TableRow key={"nothing found wr"}>
              <StyledTableCell colSpan='14' key={"nothing found cells wr"}>
                <p>{noDataMessage}</p>
              </StyledTableCell>
          </TableRow>
          </StyledTableBody>
        )}
  </UXDataTableWithoutBody>
  </>
  );
};

export default SearchRequests;
